import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isFinite, noop } from 'lodash-es';
// Models
import {
  IncentivesWizardEntities,
  IncentivesWizardModel,
  IncentivesWizardPaths,
} from 'client/data/models/incentives-wizard';
import { InventoryEntities } from 'client/data/models/inventory';
// Utils
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { getCreativeId } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-creative-id';
import { wizardTrackingIds } from 'client/tracking/maps/incentives-wizard';
import { getPotentialRebatesSum } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-potential-rebates-sum';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { getPublicationStateByType } from 'site-modules/shared/utils/publication-states';
import { getTotalRebateAmount } from 'site-modules/shared/utils/incentives/get-rebate-amount-from-incentives';
import { buildEligibleIncentives } from 'site-modules/shared/components/incentives/incentives-wizard/utils/build-eligible-incentives';
// Constants
import { STEP_FACTORY_COMPONENTS_MAP } from 'site-modules/shared/components/incentives/incentives-wizard/factories/step-factory-components-map';
// Components
import { Button } from 'site-modules/shared/components/inventory/button/button';
import { IncentivesWizardDrawer } from 'site-modules/shared/components/inventory/incentives-wizard/incentives-wizard-drawer/incentives-wizard-drawer';
import { PersistedStateWizardContentAsync } from 'site-modules/shared/components/inventory/incentives-wizard/wizard-content/persisted-state-wizard-content-async';

import './incentives-ev-insight-wizard.scss';

function IncentivesEvInsightWizardUI({
  incentives,
  vinData,
  setModelValue,
  isMobile = false,
  wizardState,
  onFinish,
  onStartOver = noop,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [setDrawerTimeout] = useTimeout();

  const engineTypes = [get(vinData, 'vehicleInfo.partsInfo.engineType', '').toLowerCase()];
  const inventoryCode = get(vinData, 'type');
  const { make, model, year } = get(vinData, 'vehicleInfo.styleInfo', {});
  const dealerZip = get(vinData, 'dealerInfo.address.zip');
  const dealerStateCode = get(vinData, 'dealerInfo.address.stateCode');
  const bodyType = get(vinData, 'vehicleInfo.styleInfo.bodyType');
  const vin = vinData.vin;
  const totalRebates = getPotentialRebatesSum(incentives);

  const onToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleButtonClick = useCallback(() => {
    setDrawerTimeout(async () => {
      await setModelValue(IncentivesWizardPaths.getVehicle(), IncentivesWizardModel, {
        inventoryCode: getPublicationStateByType(inventoryCode).toUpperCase(),
        engineTypes,
        year,
        makeSlug: makeNiceName(make),
        modelSlug: makeNiceName(model),
        vin,
      });
      await setModelValue(IncentivesWizardPaths.getIncentivesWizardFilters(), IncentivesWizardModel, {
        incentiveFilters: {
          zip: dealerZip,
          stateCode: dealerStateCode,
        },
      });
      await setModelValue(IncentivesWizardPaths.getIncentivesWizardData(), IncentivesWizardModel, incentives);
      setIsOpen(prev => !prev);
    }, 0);
  }, [
    setDrawerTimeout,
    setModelValue,
    inventoryCode,
    engineTypes,
    year,
    make,
    model,
    vin,
    dealerZip,
    dealerStateCode,
    incentives,
  ]);

  const isNoRebates = totalRebates === 0;
  const creativeId = getCreativeId({ inventoryType: getPublicationStateByType(inventoryCode), incentives });
  const openWizardButtonText = isEmpty(wizardState) ? 'Check Your Eligibility' : 'View Rebates';

  // TODO: use async WizardContentAsync component when it is fixed for drawer on vdp
  return (
    <div className="incentives-ev-insight-wizard" data-tracking-parent={creativeId}>
      <div className="p-1 rounded-12 bg-blue-100 text-center">
        {isFinite(totalRebates) && (
          <div className="total-rebates-value fw-bold size-30">
            {isEmpty(wizardState)
              ? formatPriceString(totalRebates)
              : formatPriceString(
                  getTotalRebateAmount(buildEligibleIncentives(incentives, wizardState.eligibleIncentiveIds))
                )}
          </div>
        )}
        <div className="text-cool-gray-30 medium fw-medium">
          {isNoRebates ? 'None currently available' : 'Your potential savings'}
        </div>
      </div>
      {!isNoRebates && (
        <div className="mt-1_5">
          <Button
            color="outline-blue-50"
            size="lg"
            onClick={handleButtonClick}
            className="text-transform-none fw-medium py-0_5 w-100"
            data-tracking-id={wizardTrackingIds.openIncentivesMatcher}
            data-tracking-value={openWizardButtonText}
          >
            {openWizardButtonText}
          </Button>
        </div>
      )}
      <IncentivesWizardDrawer
        onToggle={onToggle}
        isOpen={isOpen}
        isMobile={isMobile}
        make={make}
        model={model}
        year={year}
        bodyType={bodyType}
        engineTypes={engineTypes}
        type={getPublicationStateByType(inventoryCode)}
        stepFactoryComponentsMap={STEP_FACTORY_COMPONENTS_MAP}
        component={PersistedStateWizardContentAsync}
        creativeId={creativeId}
        initialStepIndex={isEmpty(wizardState) ? 0 : -1}
        initialEligibleIncentivesIds={wizardState?.eligibleIncentiveIds}
        onRebatesSaveSum={onFinish}
        savedAnswers={wizardState?.answers}
        onStartOver={onStartOver}
      />
    </div>
  );
}

IncentivesEvInsightWizardUI.propTypes = {
  incentives: IncentivesWizardEntities.IncentivesData.isRequired,
  vinData: InventoryEntities.InventoryVin.isRequired,
  setModelValue: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  wizardState: IncentivesWizardEntities.IncentivesWizardUnwrappedPersistedState,
  onStartOver: PropTypes.func,
};

export const IncentivesEvInsightWizard = connectToModel(IncentivesEvInsightWizardUI, {});
